/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React, { useMemo, useState } from "react";
import PriorityHighRoundedIcon from "@mui/icons-material/PriorityHighRounded";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import { Doughnut } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";
import Tooltip from "@mui/material/Tooltip";


import { Dialog, DialogContent } from '@mui/material';

// Soft UI Dashboard PRO React components
import SoftBox from "../../../../components/SoftBox";
import SoftTypography from "../../../../components/SoftTypography";
import SoftButton from "../../../../components/SoftButton";

// Soft UI Dashboard PRO React example components
import ReportsDoughnutChartItem from "./ReportsDoughnutChartItem";
import { SET_PLAYER_STATUS } from "../../../../api/user";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

// ReportsDoughnutChart configurations
import configs from "./configs";
import { useNavigate } from "react-router-dom";

function ReportsDoughnutChart({ title, count, chart, link, userId, tooltip, playerName, playerStatus }) {
  const { data, options } = configs(chart.labels || [], chart.datasets || {});
  const [openDialog, setDialog] = useState(false);
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(playerStatus === false ? 1 : 0);

  const handleChange = async (e, value) => {
    setTabValue(value);
    const body = {
      userId,
      status: value,
    };
    const response = await SET_PLAYER_STATUS(body);
    if (response.status === 200) {
      setDialog(true);
    }
  };

  const closeDialog = () => {
    setDialog(false);
    window.location.reload();
  };

  const renderItems =
    chart.labels && chart.datasets
      ? chart.labels.map((label, key) => (
          <ReportsDoughnutChartItem
            color={
              options.backgroundColors ? options.backgroundColors[key] : "dark"
            }
            title={label}
            id={chart.ids?.[key]}
            type={chart.types?.[key]}
            normalValue={chart.normalValues?.[key]}
            pointsValue={chart.pointsValue?.[key]}
            key={label}
            item={chart.labelItems?.[key]}
            percentage={`${
              chart.datasets.data ? chart.datasets.data[key] : 0
            }%`}
            isYeti={chart.isYeti?.[key]}
          />
        ))
      : null;

  return (
    <Card>
      <SoftBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        pt={2}
        px={{ mini: 0, xxs: 1, sm: 2 }}
      >
        <SoftBox 
          display="flex"
          justifyContent="space-between"
          alignItems="center">
          <SoftTypography variant="h6">{title}</SoftTypography>
          {userId && (
            <Tabs
              value={tabValue}
              onChange={handleChange}
              sx={{ 
                py: "2px", 
                px: "2px", 
                marginLeft: "1rem",
                backgroundColor: tabValue === 0 ? "#EAECF0" : "#B7DCBA",
                "& .MuiTabs-indicator": {
                  background: tabValue === 0 ? "#98A2B3" : "#299E47",
                },
              }}
            >
              <Tab
                label={tabValue === 0 ? "" : "Show"}
                sx={{ fontSize: "0.5rem" }}
              />
              <Tab
                label={tabValue === 1 ? "" : "Hide"} 
                sx={{ fontSize: "0.5rem" }}
              />
            </Tabs>
          )}
        </SoftBox>
        {link && (
          <Tooltip title={tooltip} placement="bottom" arrow>
            <SoftButton
              variant="outlined"
              color="secondary"
              size="small"
              circular
              iconOnly
              onClick={() => navigate(`/scorekeeper/timeline?user=${userId}`)}
            >
              <PriorityHighRoundedIcon />
            </SoftButton>
          </Tooltip>
        )}
      </SoftBox>
      <SoftBox
        sx={{
          paddingX: { mini: 0, xxs: 1, sm: 2 },
          paddingY: { mini: 1, xxs: 2 },
        }}
      >
        {useMemo(
          () => (
            // Compact layout: a FIXED-size doughnut with the total inside,
            // KPI legend beside it. The old version let the canvas fill the
            // whole card (the giant circle QA bug) and offset the count with
            // -5.8rem hacks that broke at small widths. Flex-wrap stacks the
            // legend under the chart on narrow cards — nothing clips.
            <SoftBox
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
                gap: { mini: "8px", sm: "20px" },
              }}
            >
              <SoftBox
                sx={{
                  position: "relative",
                  width: "160px",
                  height: "160px",
                  flexShrink: 0,
                }}
              >
                <Doughnut data={data} options={options} />
                <SoftBox
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    pointerEvents: "none",
                  }}
                >
                  <SoftTypography
                    sx={{
                      fontFamily: "DM Sans",
                      fontSize: "22px",
                      fontWeight: 600,
                      color: "#101828",
                      lineHeight: 1.1,
                      fontFeatureSettings: '"tnum" 1',
                    }}
                  >
                    {count.number}
                  </SoftTypography>
                  <SoftTypography
                    sx={{
                      fontFamily: "DM Sans",
                      fontSize: "10.5px",
                      fontWeight: 500,
                      color: "#98A2B3",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {count.text}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
              <SoftBox sx={{ flex: 1, minWidth: "230px", maxWidth: "420px" }}>
                {renderItems}
              </SoftBox>
            </SoftBox>
          ),
          [chart],
        )}
      </SoftBox>
      <Dialog
        open={openDialog}
        onClose={() => setDialog(false)}
        sx={{
          ".MuiPaper-root": {
            borderRadius: "8px",
            background: '#FFFFFF',
            padding: '1.25rem',
            maxWidth: { mini: "90%", xs: "90%", sm: "400px" },
            width: "400px",
          },
        }}
      >
        <SoftBox display="flex" alignItems="center" sx={{ padding: "0 !important" }}>
          <SoftBox display="flex" alignItems="center">
            <SoftBox display="flex" flexDirection="column">
              <SoftTypography
                component="h3"
                sx={{
                  fontSize: "18px",
                  color: "#000000",
                  margin: 0,
                  padding: 0,
                  fontWeight: "500",
                }}
              >
                {tabValue === 0 ? "Results Shown" : "Results Hidden"}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </SoftBox>
        <DialogContent sx={{ padding: "0 !important" }}>
          <SoftTypography sx={{
            fontSize: "14px",
            color: "#000000",
            margin: 0,
            padding: 0,
            fontWeight: "400",
            marginTop: "0.25rem",
          }}>
            {playerName} scores have been {tabValue === 0 ? "included" : "excluded"}. Team's average<br /> score recalculated.
          </SoftTypography>
          <SoftBox display="flex" justifyContent="flex-end">
            <SoftButton
              onClick={() => closeDialog()}
              color="primary"
              sx={{
                marginTop: "1.5rem",
                fontSize: "14px",
                fontWeight: "500",
                textTransform: "capitalize",
                marginRight: "0.25rem",
                padding: "0.5rem 1rem",
                "&:hover": {
                  padding: "0.5rem 1rem",
                },
              }}
            >
              Okay
            </SoftButton>
          </SoftBox>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Setting default values for the props of ReportsDoughnutChart
ReportsDoughnutChart.defaultProps = {
  link: false,
};

// Typechecking props for the ReportsDoughnutChart
ReportsDoughnutChart.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.shape({
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    text: PropTypes.string.isRequired,
  }).isRequired,
  chart: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.objectOf(
      PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
        PropTypes.string,
      ]),
    ).isRequired,
  }).isRequired,
  link: PropTypes.bool,
};

export default ReportsDoughnutChart;
